/**
 * 入住后的到家等待页（/arriving，bare 路由；用户 10-03 原话：等姿态都生成完毕后再到家，不要只看到贴图）。
 * 依据 c84a D0742-2、D0812-1、D0842-1、D0912-2 与 6c2b 16:4x／16:5x／17:0x／17:3x：
 * - 只有完整（arrivingModel.arrivalPhase 判为 arrived）才自动 replace 去 /home 并带 { arrival: petId }；小窝的到家时刻只播这一次。
 *   这一页**没有任何别的去小窝的路**：没齐的首次到家只能去星球（观察室）或重读进度，回来接着等原任务（D0842-1）。
 * - 没到家的每个阶段都给「先去星球看看」（去 /world）；读不到时再加「再看一次」（只重读 GET）。
 * - 没画成按原因码分出路（CREATE-PET-REGEN-01，I 10-09 定；WALK-I107 D1：原来只有「先去星球看看」，后端给的 can_regenerate 没用上）：
 *   「再试一次」（能力表开着、服务端 can_regenerate、不是名额满）、「换一张照片」（只给 subject_too_large，去 TA 的形象页换；
 *   photo_* 查的是画出来的证件照成品、不是主人的照片，I 10-09 定并进再试一次），见 arrivingModel.failedRouteOf；
 *   两个都没有时照实说先用上传的照片、过一会儿再来试试。「先去星球看看」一直都在。
 * - 给玩家看的字顺着「找到 TA · 传输意识 · 带回样子」的比喻，不露「画」（HOME-FIGURE-APPEAR-01，I 10-09 看截图定）：没画成说「这次没能带回来」，
 *   按钮叫「再试一次」（原「再画一次」）；名额满、排队等名额、会自动再试这几句也一起改（FINDER-FIX，I 10-09 同意；6796 待办已由 I 记），
 *   事实照实说（名额满了、在排队、会自动再试），不许诺时间。代码里的名字（go: redraw、useRedraw、REDRAW_*）照旧，只是内部叫法。
 * - 文案不做任何费用保证（D0912-2）：只说名额满了、没能开始带回，不说没扣、没用掉次数；再试一次也一样，不提名额怎么算（I 10-09）。
 * - 不设总时限、不自动退出、不自动回家；读的频率等久了放慢。
 * - 能离开、能回来：地址补 ?pet=<pet_id>；从星球回来、刷新、或小窝气泡带着这个地址进来，都只读同一只的状态，不触发任何生图。
 * - 页面只调 GET，不自己发重画：首套和系统自动姿态不能变成玩家付费重画。唯一的例外是没画成时主人自己点「再试一次」（见 useRedraw）：
 *   发一次 POST …/character/regenerate，带幂等键、防连点，结果不明不自动重发。
 * - 演示模式：不看会话和能力表，读演示版的形象（features/pets 的演示服务只给这一页演一段：没能带回来 → 再试一次 → 到家）。
 */
import { bodyDetailsOf, bodyQueryKey } from "@/features/me/bodyInfoModel";
import { checkedBody } from "@/features/me/BodyInfo";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useSessionState } from "@/shared/session/onboarding";
import { Button } from "@/shared/ui";
import { WARP_GRACE_MS, WARP_MAX_MS, WarpStreaks } from "@/shared/ui/WarpStreaks";
import type { PlanetGlobeHandle, PlanetSpaceOptions } from "@/features/pets/planet3d/PlanetGlobe";
import { canReplacePhoto } from "@/features/me/LookPage";
import { ARRIVED_BEAT_MS, arrivalPhase, arrivingPetOf, failedRouteOf, isWaiting, nextCheckTime, pollInterval, requiredPosesOf, stageOf, type ArrivalGate, type ArrivalPhase } from "./arrivingModel";
import "./arriving.css";

const STEPS = ["找到 TA", "传输意识", "带回样子"] as const;
/** UI-SPACE-01b：等待时的宇宙地球（和 /world 首访落地同一个 3D 地球，按需加载；点它不进去） */
const PlanetGlobe = lazy(() => import("@/features/pets/planet3d/PlanetGlobe"));
const SPACE: PlanetSpaceOptions = { farTextureUrl: "/ui-assets/OBS-SPACE-01/v1/planet-night-2048.png", backdropUrl: "/ui-assets/OBS-SPACE-01/v1/space-backdrop.jpg", tapToEnter: false, durationMs: 1600 };
/** 星线跃迁时长（REQ13 §5 约 1.2–2 秒） */
export const ARRIVE_WARP_MS = 1600;
const noop = () => {};
function hasWebGL(): boolean {
  try {
    const probe = document.createElement("canvas");
    const ctx = (probe.getContext("webgl2") ?? probe.getContext("webgl")) as WebGLRenderingContext | null;
    if (!ctx) return false;
    ctx.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}
const QUOTA_LINE = "可以先去星球看看，稍后再回来。";
const EXIT_LINE = "可以先去星球转转，稍后再回来看看。";
const WAIT_LINE = "结果还没传回来，这里会接着看。";
/**
 * 没画成时的说法（CREATE-PET-REGEN-01，I 10-09）：温和、不怪主人，不露原因码，不说“生成失败”这类工程话，也不提名额、费用怎么算。
 * 标题有没有出路都说“这次没能带回来”（HOME-FIGURE-APPEAR-01），下一句按给了哪几条路说；两条都没有时照实说先用上传的照片、过一会儿再来试试。
 */
const REDRAW_LINE = "再试一次吧，也可以先去星球转转。";
const REDRAW_THEN_PHOTO_LINE = "可以再试一次，也可以换一张照片试试。";
const PHOTO_LINE = "换一张照片试试吧。";
const LATER_FIGURE_LINE = "先用你上传的照片，过一会儿再来试试。";
const LATER_POSES_LINE = "过一会儿再来试试，可以先去星球转转。";
/** 换照片的建议（I 10-09 举的例子）：只在真给了「换一张照片」时说 */
export const PHOTO_TIP = "照片里最好有 TA 的全身，离镜头远一点，背景简单些。";
/** 点了「再试一次」以后：服务端回了“没排上”（排上了、或已经在画了不说，页面自己回到等待） */
export const REDRAW_NOT_STARTED = "这次没能开始，可以过一会儿再试。";
/** 点了「再试一次」以后没有回音（断网、超时、服务端出错）：不自动重发；主人再点沿用同一把幂等键，后端认出是同一次。说法里的按钮名要和按钮一字不差 */
export const REDRAW_UNSURE = "还没确认这一次开始了没有。可以再点一次「再试一次」，不会重复。";

type Copy = { title: string; line: string };

/** 没画成时这一页此刻真能给的出路（「先去星球看看」另算，一直都在）；photo 只有 subject_too_large 才可能为真 */
type Outs = { redraw: boolean; photo: boolean; body?: "edit" | "loading" | "wait" };

/** 文案（6c2b 定；A 原因码对照表 17:0x）：不放数字、代码、AI 字样，不提重画和几点 */
function copyOf(phase: ArrivalPhase, outs: Outs): Copy {
  switch (phase.kind) {
    case "checking":
      return { title: "正在寻找 TA…", line: "马上就好。" };
    case "finding":
      return { title: "正在寻找 TA…", line: "在星球上找 TA 的气息。" };
    case "quotaWait":
      return { title: "正在排队等名额，有名额后接着带回 TA 的样子", line: QUOTA_LINE };
    case "transferring":
      return { title: "正在传输意识…", line: "把 TA 的样子和脾气，一点点带过来。" };
    case "retrying":
      return { title: "这次没能带回来，会自动再试一次", line: "这里会接着看。" };
    case "bringing":
      return phase.quotaWait
        ? { title: "正在带回 TA 的样子…", line: `其余的样子在排队等名额，有名额后继续。${QUOTA_LINE}` }
        : { title: "正在带回 TA 的样子…", line: "TA 的其他样子也在一起带过来。" };
    case "confirming":
      return { title: "还在确认 TA 的样子", line: WAIT_LINE };
    case "arrived":
      return { title: "TA 到家了", line: "推开门看看吧。" };
    case "failed": {
      if (outs.body) return { title: "TA 还没带回全身", line: outs.body === "wait" ? "还差一点 TA 的身体特点，等家里人补上才能带回 TA 的全身。" : "还差一点 TA 的身体特点，补上才能带回 TA 的全身。" };
      // 中性形象没画成说「TA 的样子」，必要姿态没画成说「TA 还有几个样子」（中性已经在了）
      const what = phase.scope === "figure" ? "TA 的样子" : "TA 还有几个样子";
      if (phase.dayFull) return { title: "今天带回样子的名额满了", line: `${what}这次没能开始带回。${EXIT_LINE}` };
      // 两条路都没有：照实说（中性形象没画成时小窝先用主人上传的照片；姿态没画成时中性已经在了，只说过一会儿再来）
      if (!outs.redraw && !outs.photo) return { title: `${what}这次没能带回来`, line: phase.scope === "figure" ? LATER_FIGURE_LINE : LATER_POSES_LINE };
      const line = !outs.photo ? REDRAW_LINE : !outs.redraw ? PHOTO_LINE : REDRAW_THEN_PHOTO_LINE;
      return { title: `${what}这次没能带回来`, line };
    }
    case "blocked":
      return { title: phase.notConfigured ? "现在还带不回 TA 的样子" : "TA 的样子暂时带不回来", line: EXIT_LINE };
    case "unreachable":
      return { title: "暂时看不到 TA 的进度", line: "可以再看一次，也可以先去星球看看。" };
  }
}

/** 标题按逗号分段、段内不折行：窄屏折行时不会只剩一个字掉到下一行 */
function TitleLine({ text }: { text: string }) {
  const parts = text.match(/[^，]*，|[^，]+$/g) ?? [text];
  if (parts.length < 2) return <>{text}</>;
  return <>{parts.map((part, index) => <span key={index} className="ps-arrive__seg">{part}</span>)}</>;
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type Action = { label: string; go: "world" | "retry" | "redraw" | "photo"; primary: boolean };

/**
 * 没到家的每个阶段只给「先去星球看看」；读不到时「再看一次」在前；到家时没有按钮（自动走）。没有去小窝的按钮（D0842-1）。
 * 没画成（CREATE-PET-REGEN-01）：能给的出路排在前面，第一条是主按钮——一律「再试一次」在前，「换一张照片」（只有 subject_too_large）其后；
 * 「先去星球看看」放最后。
 */
function actionsOf(phase: ArrivalPhase, outs: Outs): Action[] {
  const world = (primary: boolean): Action => ({ label: "先去星球看看", go: "world", primary });
  switch (phase.kind) {
    case "arrived":
      return [];
    case "unreachable":
      return [{ label: "再看一次", go: "retry", primary: true }, world(false)];
    case "failed": {
      if (outs.body) return [world(outs.body !== "edit")];
      const redraw: Action | null = outs.redraw ? { label: "再试一次", go: "redraw", primary: false } : null;
      const photo: Action | null = outs.photo ? { label: "换一张照片", go: "photo", primary: false } : null;
      const outsFirst = [redraw, photo].filter((a): a is Action => a !== null);
      return outsFirst.length ? [...outsFirst.map((a, i) => ({ ...a, primary: i === 0 })), world(false)] : [world(true)];
    }
    default:
      return [world(true)];
  }
}

/**
 * 再试一次（CREATE-PET-REGEN-01；按钮原叫「再画一次」，HOME-FIGURE-APPEAR-01 改名）：和「调整形象」（home/PetFigure.tsx 的 AdjustCharacter）同一个调用——
 * pets.regenerateCharacter，正文 { pose: "neutral_full", note: null }，带幂等键（同一个前缀 character-regenerate），完了刷新同一条形象缓存。
 * - 一次点击一把键；有回音（排上了或没排上）才换新键；没回音（断网、超时、服务端出错）保留同一把，**不自动重发**——主人再点沿用同一把，后端认出是同一次。
 * - 防连点：发出去到有结果之前按钮是 loading（不能点）；同一拍里连点两下也只发一次（sending）。
 * - 不论有没有回音都重读一次形象（只读 GET）：排上了页面就回到等待，没排上照新状态说。
 * - 说法见 REDRAW_NOT_STARTED、REDRAW_UNSURE：不露原因码，不提名额、费用怎么算。
 */
function useRedraw(petId: string | null, userId: string | null) {
  const { pets } = useServices();
  const queryClient = useQueryClient();
  const keyRef = useRef(newIdempotencyKey("character-regenerate"));
  const sending = useRef(false);
  const [said, setSaid] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (id: string) => pets.regenerateCharacter(id, { pose: "neutral_full", note: null }, keyRef.current),
    onSuccess: (result) => {
      keyRef.current = newIdempotencyKey("character-regenerate");
      setSaid(result.accepted || result.reason === "already_queued" ? null : REDRAW_NOT_STARTED);
    },
    onError: () => setSaid(REDRAW_UNSURE),
    onSettled: () => {
      sending.current = false;
      void queryClient.invalidateQueries({ queryKey: queryKeys.characterFor(userId ?? "-", petId ?? "-") });
    },
  });
  const start = () => {
    if (sending.current || !petId) return;
    sending.current = true;
    setSaid(null);
    mutation.mutate(petId);
  };
  return { start, pending: mutation.isPending, said };
}

export function ArrivingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pets, platform, households } = useServices();
  const routeState = (location.state ?? null) as { arrival?: unknown } | null;
  const fromState = typeof routeState?.arrival === "string" ? routeState.arrival : null;
  const fromQuery = new URLSearchParams(location.search).get("pet");
  const petId = fromState ?? fromQuery;

  // 地址上补 ?pet=：从星球回来、刷新都能接着等同一只（只读，不触发生图）
  useEffect(() => {
    if (petId && !fromQuery) navigate({ pathname: location.pathname, search: `?pet=${encodeURIComponent(petId)}` }, { replace: true, state: location.state });
  }, [petId, fromQuery]);

  const session = useSessionState();
  const userId = session.data?.user?.user_id ?? null;
  const live = env.dataMode === "live";
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: live && Boolean(petId) });
  const capability = meta.data?.capabilities.find((c) => c.key === "character.state")?.status ?? null;
  // 读不到的都算 skip（照实说读不到，不自动回家）：没有宠物号、会话或元数据读失败、能力表里没有可用的形象状态。
  // 演示模式不看会话和能力表，按宠物号读演示版的形象（CREATE-PET-REGEN-01：演示要能走通没画好 → 再画一次）
  const gate: ArrivalGate = !petId || (live && (meta.isError || session.isError)) ? "skip" : !live ? "wait" : meta.isPending || session.isPending ? "loading" : capability === "available" && userId ? "wait" : "skip";

  const startedAt = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [interval, setPoll] = useState<number | false>(false);
  const character = useQuery({
    queryKey: queryKeys.characterFor(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => pets.character(petId!, signal),
    enabled: gate === "wait",
    retry: false,
    refetchInterval: interval,
    refetchIntervalInBackground: false,
  });
  // 连续读失败的次数：读成功一次、或主人点了「再看一次」，就从那时的失败计数重新数
  const [errorBase, setErrorBase] = useState(0);
  useEffect(() => setErrorBase(character.errorUpdateCount), [character.dataUpdatedAt]);

  const elapsedMs = now - startedAt.current;
  const requiredPoses = requiredPosesOf(character.data);
  const phase = arrivalPhase({ gate, state: character.data, petId, requiredPoses, elapsedMs, readErrors: character.errorUpdateCount - errorBase });
  const poll = pollInterval(phase, elapsedMs);
  useEffect(() => setPoll(poll), [poll]);

  // 没画成时的出路（CREATE-PET-REGEN-01）。再试一次：能力表开着（演示模式不看能力表）、服务端 can_regenerate、不是名额满
  const failed = phase.kind === "failed" ? phase : null;
  const route = failedRouteOf(failed?.reason);
  const regenerateStatus = meta.data?.capabilities.find((c) => c.key === "character.regenerate")?.status ?? null;
  const bodyMissing = failed?.reason === "body_info_needed" && bodyDetailsOf(character.data) !== null;
  const bodyAvailable = live && Boolean(userId) && meta.data?.capabilities.some(c => c.key === "pets.body_profile" && c.status === "available") === true && typeof pets.bodyProfile === "function";
  const body = useQuery({ queryKey: bodyQueryKey(userId, petId ?? ""), enabled: bodyMissing && bodyAvailable, queryFn: async ({signal}) => checkedBody(await pets.bodyProfile!(petId!, signal), petId!), retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false, staleTime: 15_000 });
  const redrawOn = !bodyMissing && failed !== null && !failed.dayFull && (!live || regenerateStatus === "available") && character.data?.can_regenerate === true;
  // 换一张照片：只给 subject_too_large；去 TA 的形象页换。要这一只在 /households 里（形象页凭 ?pet= 才认得出），条件同形象页
  const wantsPhoto = failed !== null && !failed.dayFull && route === "tooLarge";
  const homes = useQuery({ queryKey: queryKeys.households(userId ?? "-"), queryFn: () => households.list(), enabled: live && Boolean(userId) && wantsPhoto, staleTime: 15_000 });
  const brief = wantsPhoto ? arrivingPetOf(homes.data, petId) : null;
  const photoOn = brief !== null && canReplacePhoto(brief.photo_url ?? null, brief.origin);
  const outs: Outs = { redraw: redrawOn, photo: photoOn, body: bodyMissing ? !bodyAvailable || body.isError ? "wait" : body.isPending ? "loading" : body.data?.can_edit && typeof pets.submitBody === "function" ? "edit" : "wait" : undefined };
  const redraw = useRedraw(petId, userId);

  // 计时：只在还要等的时候每秒走一下（放慢读的频率、核「下一次检查」有没有过期；不退出、不跳转）
  const waiting = isWaiting(phase);
  useEffect(() => {
    if (!waiting) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [waiting]);

  // 自动到家只有一种：完整了（先说一声「TA 到家了」再走；减少动态时不停）。
  // UI-SPACE-01b：完整以后地球拉近＋星线跃迁；跃迁播完、且「TA 到家了」至少停了 ARRIVED_BEAT_MS，才进家。
  // 一定落地：跃迁组件自己有兜底；这里再给一道总上限；点画面或按 Esc 直接进家；只走一次。
  const [webgl, setWebgl] = useState(hasWebGL);
  const globeRef = useRef<PlanetGlobeHandle | null>(null);
  const [warping, setWarping] = useState(false);
  const went = useRef(false);
  const warpDone = useRef(false);
  const beatDone = useRef(false);
  const goHome = useCallback(() => {
    if (went.current || !petId) return;
    went.current = true;
    navigate("/home", { replace: true, state: { arrival: petId } });
  }, [navigate, petId]);
  useEffect(() => {
    if (phase.kind !== "arrived" || !petId) return;
    if (prefersReducedMotion()) {
      const id = window.setTimeout(goHome, 0);
      return () => window.clearTimeout(id);
    }
    setWarping(true);
    globeRef.current?.enterSpace();
    const beat = window.setTimeout(() => {
      beatDone.current = true;
      if (warpDone.current) goHome();
    }, ARRIVED_BEAT_MS);
    const cap = window.setTimeout(goHome, ARRIVED_BEAT_MS + WARP_MAX_MS + WARP_GRACE_MS);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") goHome();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(beat);
      window.clearTimeout(cap);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind]);
  const onWarpDone = useCallback(() => {
    warpDone.current = true;
    if (beatDone.current) goHome();
  }, [goHome]);

  const act = (go: Action["go"]) => {
    if (go === "world") navigate("/world");
    else if (go === "redraw") redraw.start();
    // 换照片在 TA 的形象页（那一页有换照片和重新准备形象）；凭 ?pet= 认这一只，返回键回到这里接着等
    else if (go === "photo") navigate(`/me/look?pet=${encodeURIComponent(petId ?? "")}`);
    else {
      setErrorBase(character.errorUpdateCount);
      if (session.isError) void session.refetch();
      if (meta.isError) void meta.refetch();
      if (gate === "wait") void character.refetch();
    }
  };

  const stage = stageOf(phase);
  const copy = copyOf(phase, outs);
  const nextCheck = phase.kind === "quotaWait" ? nextCheckTime(character.data, now) : null;
  const still = phase.kind === "failed" || phase.kind === "blocked" || phase.kind === "unreachable";

  return (
    <main className="ps-arrive ps-night" data-warp={warping ? "run" : undefined} onClick={warping ? goHome : undefined}>
      {/* 没在进行（没画成、暂时画不了、联系不上）时不挂会转的地球，换静态星球：不拿动画假装还在忙（c84a D0742-2，原来那团光的规矩） */}
      <div className="ps-arrive__space" aria-hidden="true" data-testid="arrive-space" data-still={still ? "" : undefined}>
        {webgl && !still ? (
          <Suspense fallback={<div className="ps-arrive__space-still" />}>
            <PlanetGlobe residents={[]} coordSystem="wgs84" onSelect={noop} onRegion={noop} onUnavailable={() => setWebgl(false)} label="" space={SPACE} ref={globeRef} />
          </Suspense>
        ) : (
          <div className="ps-arrive__space-still" data-testid="arrive-space-still" />
        )}
        {warping ? <WarpStreaks run durationMs={ARRIVE_WARP_MS} onDone={onWarpDone} className="ps-arrive__warp" /> : null}
      </div>
      <div className="ps-arrive__body">
        <div role="status" aria-live="polite" className="ps-arrive__status">
          <h1 className="ps-arrive__title"><TitleLine text={copy.title} /></h1>
          <p className="ps-arrive__line">{copy.line}</p>
          {nextCheck ? <p className="ps-arrive__note">下一次检查：{nextCheck}</p> : null}
          {failed && outs.photo ? <p className="ps-arrive__note">{PHOTO_TIP}</p> : null}
          {failed && redraw.said ? (
            <p className="ps-arrive__note" data-motion="enter">
              {redraw.said}
            </p>
          ) : null}
        </div>
        {stage !== null && stage < 3 ? (
          <ol className="ps-arrive__steps" aria-label="到家的进度">
            {STEPS.map((label, i) => (
              <li key={label} className={`ps-arrive__step${i < stage ? " ps-arrive__step--done" : i === stage ? " ps-arrive__step--now" : ""}`} aria-current={i === stage ? "step" : undefined}>
                {label}
              </li>
            ))}
          </ol>
        ) : null}
        {phase.kind === "bringing" && phase.total > 0 ? (
          <ul className="ps-arrive__dots" aria-hidden="true">
            {Array.from({ length: phase.total }, (_, i) => (
              <li key={i} className={`ps-arrive__dot${i < phase.ready ? " ps-arrive__dot--ready" : ""}`} />
            ))}
          </ul>
        ) : null}
        {phase.kind !== "arrived" ? (
          <div className="ps-arrive__actions">
            {outs.body === "edit" && petId ? <Link className="ps-btn ps-btn--primary ps-btn--block" to={`/me/look?pet=${encodeURIComponent(petId)}`}>去补 TA 的身体特点</Link> : null}
            {actionsOf(phase, outs).map((a) => (
              <Button key={a.label} variant={a.primary ? "primary" : "secondary"} block loading={a.go === "redraw" && redraw.pending} onClick={() => act(a.go)}>
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </main>
  );
}
